export default function BrokenArrowTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Broken Arrow Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Broken Arrow travelers actually move helps us anticipate the passport urgencies that arise here. From church mission group coordination to Rose District business travel, the Tulsa east metro operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* TUL Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">TUL — Tulsa International Airport</h3>
                <p className="text-gray-400 text-xs">Northeast Tulsa, about 15 miles from downtown Broken Arrow</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Tulsa International Airport (TUL) serves the entire Tulsa metro including Broken Arrow. Nonstop service to major domestic hubs including Dallas, Denver, Chicago, Atlanta, and Houston — with international connections throughout. Broken Arrow families frequently use TUL for Cancun charters and Caribbean cruises. The airport's compact size means check-in can be quick, but that does not help if your passport is expired.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many Broken Arrow travelers book international flights through Dallas or Houston hubs. Even if your first leg is domestic, international connections require a valid passport — check yours before booking.
              </p>
            </div>
          </div>

          {/* Church Mission Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Church Mission Trips &amp; Group Travel</h3>
                <p className="text-gray-400 text-xs">Broken Arrow, Bixby, Jenks &amp; Coweta churches</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Broken Arrow's thriving faith community generates significant group passport demand, particularly in spring and summer. Local churches in Broken Arrow, Bixby, and Jenks organize group trips to Guatemala, Honduras, and Costa Rica. Youth group leaders often confirm participant rosters 4–6 weeks before departure, then discover that multiple passports have expired or that children have never had one. We coordinate group processing and child passport requirements to keep entire mission teams on schedule.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Church group leaders: audit every participant's passport as soon as the travel roster is finalized — not the week before departure. Child passports require both parents to appear together or submit a notarized consent form.
              </p>
            </div>
          </div>

          {/* Family & Holiday Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-home-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family Vacations &amp; Holiday Travel</h3>
                <p className="text-gray-400 text-xs">Rose District families, Bixby, Coweta &amp; Wagoner</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Broken Arrow families are some of the most travel-active in Oklahoma. Spring break trips to Cancun and Jamaica, summer European cruises, and holiday ski trips to Canada create predictable seasonal demand. Many families discover expired passports when booking Christmas trips in November — or when the kids' passports expired while no one was checking. The 6-month validity rule catches many BA families by surprise.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check every family member's passport at the start of the year — not when you're booking. Children's passports expire in 5 years, and many families don't realize their child's passport is expired until the cruise booking screen rejects it.
              </p>
            </div>
          </div>

          {/* NSU-BA & College Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">NSU-BA &amp; College Student Travel</h3>
                <p className="text-gray-400 text-xs">Northeastern State University — Broken Arrow Campus</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              NSU-BA's nursing, education, and business programs send students abroad for clinical rotations, teaching practicums, and international business programs. Study-abroad application deadlines typically fall in October and February, but many students do not think about their passport until after acceptance. First-time applicants (many college students) require an in-person acceptance clerk visit. We guide NSU-BA students through the exact Broken Arrow location, forms, and timeline so they do not miss their program orientation.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many NSU-BA students) require an in-person acceptance clerk visit. We guide BA students through the exact location, forms, and timeline so they don't miss their program orientation.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">BA Corporate &amp; Business Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Broken Arrow's growing business community — from Warren Clinic professionals to Rose District entrepreneurs to manufacturers in the BA industrial park — generates international business travel. Short-notice conferences, client visits, and partnership meetings in Canada, Europe, and Asia require passports that are ready when the opportunity arises. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}